import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Music, Play, Info, Volume2, BookOpen, Ear, Piano, Disc, X, CheckCircle } from 'lucide-react';
import { Card } from '../components/Card';
interface InstrumentModuleProps {
  onBack: () => void;
  instrumentId?: string;
}
const instrumentData: Record<string, {
  name: string;
  icon: string;
  desc: string;
  soundDesc: string;
  color: string;
  bgColor: string;
  audioFrequencies: number[];
  parts: string[];
  famousSongs: string[];
}> = {
  guzheng: {
    name: '古筝',
    icon: '🎹',
    desc: '古筝是中国传统拨弦乐器，有两千五百年以上的历史。音域宽广，音色优美，具有相当强的表现力。古筝的面板多用河南兰考的桐木制成，音箱呈长方形。',
    soundDesc: '清脆悦耳，如山涧泉水',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    audioFrequencies: [523.25, 587.33, 659.25, 783.99, 880],
    parts: ['面板', '琴弦', '琴码', '岳山', '琴尾'],
    famousSongs: ['高山流水', '渔舟唱晚', '春江花月夜']
  },
  pipa: {
    name: '琵琶',
    icon: '🪕',
    desc: "琵琶被称为'弹拨乐器之王'，已有两千多年的历史。琵琶的音箱呈半梨形，有四根弦，演奏技巧丰富，表现力极强。",
    soundDesc: '颗粒感强，大珠小珠落玉盘',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    audioFrequencies: [392, 440, 493.88, 523.25, 587.33],
    parts: ['琴头', '相位', '品位', '音箱', '琴弦'],
    famousSongs: ['十面埋伏', '阳春白雪', '大浪淘沙']
  },
  erhu: {
    name: '二胡',
    icon: '🎻',
    desc: '二胡是中国传统拉弦乐器，始于唐朝。二胡有两根弦，用马尾弓拉奏，音色柔美抒情，发音近似人声，极富表现力。',
    soundDesc: '柔美凄婉，如泣如诉',
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    audioFrequencies: [293.66, 329.63, 392, 440, 493.88],
    parts: ['琴筒', '琴杆', '琴弦', '弓子', '千斤'],
    famousSongs: ['二泉映月', '赛马', '良宵']
  },
  dizi: {
    name: '笛子',
    icon: '🪈',
    desc: '笛子是中国广为流传的吹奏乐器，一般用竹子制成。笛子有六个按孔和一个吹孔，音色清脆高亢，表现力丰富。',
    soundDesc: '清脆高亢，如鸟鸣山幽',
    color: 'text-teal-700',
    bgColor: 'bg-teal-50',
    audioFrequencies: [659.25, 783.99, 880, 987.77, 1046.5],
    parts: ['吹孔', '膜孔', '按孔', '笛膜', '笛塞'],
    famousSongs: ['姑苏行', '牧民新歌', '扬鞭催马运粮忙']
  },
  guqin: {
    name: '古琴',
    icon: '🎸',
    desc: "古琴是中国最古老的弹拨乐器之一，有三千多年历史，位列'琴棋书画'四艺之首。古琴有七根弦，音色深沉古朴。",
    soundDesc: '深沉古朴，余音绕梁',
    color: 'text-slate-700',
    bgColor: 'bg-slate-50',
    audioFrequencies: [196, 220, 261.63, 293.66, 329.63],
    parts: ['琴面', '琴底', '琴弦', '徽位', '雁足'],
    famousSongs: ['广陵散', '流水', '梅花三弄']
  },
  bianzhong: {
    name: '编钟',
    icon: '🔔',
    desc: '编钟是中国古代大型打击乐器，兴起于西周，盛于春秋战国。编钟由多个大小不同的钟组成，用木槌敲击发声。',
    soundDesc: '宏亮深沉，金石之声',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    audioFrequencies: [261.63, 329.63, 392, 523.25, 659.25],
    parts: ['钟体', '钟架', '钟钩', '钟槌', '钟纽'],
    famousSongs: ['曾侯乙编钟乐', '楚商', '竹枝词']
  }
};
// Web Audio API sound generator
function playNote(freq: number, instrumentType: string) {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  switch (instrumentType) {
    case 'guzheng':
    case 'pipa':
    case 'guqin':
      oscillator.type = 'triangle';
      break;
    case 'erhu':
      oscillator.type = 'sawtooth';
      break;
    case 'dizi':
      oscillator.type = 'sine';
      break;
    case 'bianzhong':
      oscillator.type = 'square';
      break;
    default:
      oscillator.type = 'sine';
  }
  oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.6);
}
function playMelody(frequencies: number[], instrumentType: string) {
  frequencies.forEach((freq, index) => {
    setTimeout(() => playNote(freq, instrumentType), index * 300);
  });
}
export function InstrumentModule({
  onBack,
  instrumentId = 'guzheng'
}: InstrumentModuleProps) {
  const data = instrumentData[instrumentId] || instrumentData['guzheng'];
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentNote, setCurrentNote] = useState(-1);
  const [activeExercise, setActiveExercise] = useState<string | null>(null);
  const [quizAnswer, setQuizAnswer] = useState<number | null>(null);
  const [quizCorrect, setQuizCorrect] = useState<boolean | null>(null);
  const handlePlay = () => {
    if (isPlaying) return;
    setIsPlaying(true);
    playMelody(data.audioFrequencies, instrumentId);
    data.audioFrequencies.forEach((_, index) => {
      setTimeout(() => setCurrentNote(index), index * 300);
    });
    setTimeout(() => {
      setIsPlaying(false);
      setCurrentNote(-1);
    }, data.audioFrequencies.length * 300 + 500);
  };
  const exercises = [{
    id: 'structure',
    name: '认识结构',
    icon: BookOpen,
    desc: '了解乐器的各个部分'
  }, {
    id: 'listen',
    name: '声音辨识',
    icon: Ear,
    desc: '听音辨别不同乐器'
  }, {
    id: 'play',
    name: '简单演奏',
    icon: Piano,
    desc: '尝试弹奏简单旋律'
  }, {
    id: 'songs',
    name: '名曲欣赏',
    icon: Disc,
    desc: '聆听经典演奏曲目'
  }];
  const renderExerciseContent = () => {
    switch (activeExercise) {
      case 'structure':
        return <div className="space-y-4" data-id="element-375">
            <h4 className="font-bold text-lg text-slate-800" data-id="element-376">
              {data.name}的结构
            </h4>
            <div className="grid grid-cols-2 gap-3" data-id="element-377">
              {data.parts.map((part, i) => <motion.div key={part} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.1
            }} className={`p-4 rounded-xl ${data.bgColor} border border-slate-100`} data-id="element-378">
                  <span className="text-2xl font-bold text-slate-300 mr-2" data-id="element-379">
                    {i + 1}
                  </span>
                  <span className={`font-bold ${data.color}`} data-id="element-380">{part}</span>
                </motion.div>)}
            </div>
          </div>;
      case 'listen':
        return <div className="space-y-4" data-id="element-381">
            <h4 className="font-bold text-lg text-slate-800" data-id="element-382">听音辨器</h4>
            <p className="text-slate-500 text-sm" data-id="element-383">
              听一段音乐，猜猜是哪种乐器？
            </p>
            <button onClick={() => {
            playMelody(data.audioFrequencies.slice(0, 3), instrumentId);
          }} className="w-full py-4 bg-blue-100 text-blue-700 rounded-xl font-bold hover:bg-blue-200 transition-colors" data-id="element-384">
              🔊 播放音频
            </button>
            <div className="grid grid-cols-3 gap-3" data-id="element-385">
              {['古筝', '琵琶', '二胡'].map((name, i) => <button key={name} onClick={() => {
              setQuizAnswer(i);
              setQuizCorrect(name === data.name);
            }} className={`p-4 rounded-xl border-2 font-bold transition-all ${quizAnswer === i ? quizCorrect ? 'border-green-500 bg-green-50 text-green-700' : 'border-red-500 bg-red-50 text-red-700' : 'border-slate-200 hover:border-blue-300'}`} data-id="element-386">
                  {name}
                  {quizAnswer === i && (quizCorrect ? ' ✓' : ' ✗')}
                </button>)}
            </div>
          </div>;
      case 'play':
        return <div className="space-y-4" data-id="element-387">
            <h4 className="font-bold text-lg text-slate-800" data-id="element-388">简单演奏</h4>
            <p className="text-slate-500 text-sm" data-id="element-389">点击下方按键，弹奏一段旋律</p>
            <div className="flex justify-center gap-2" data-id="element-390">
              {['Do', 'Re', 'Mi', 'Sol', 'La'].map((note, i) => <motion.button key={note} whileHover={{
              scale: 1.05,
              y: -2
            }} whileTap={{
              scale: 0.95
            }} onClick={() => playNote(data.audioFrequencies[i], instrumentId)} className={`w-14 h-20 rounded-xl font-bold text-white shadow-lg ${i % 2 === 0 ? 'bg-blue-500' : 'bg-purple-500'}`} data-id="element-391">
                  {note}
                </motion.button>)}
            </div>
            <button onClick={() => playMelody(data.audioFrequencies, instrumentId)} className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-bold" data-id="element-392">
              🎵 播放示范旋律
            </button>
          </div>;
      case 'songs':
        return <div className="space-y-4" data-id="element-393">
            <h4 className="font-bold text-lg text-slate-800" data-id="element-394">
              {data.name}名曲
            </h4>
            <div className="space-y-3" data-id="element-395">
              {data.famousSongs.map((song, i) => <motion.button key={song} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.1
            }} onClick={() => playMelody(data.audioFrequencies, instrumentId)} className="w-full p-4 bg-white border border-slate-100 rounded-xl flex items-center justify-between hover:shadow-md transition-all group" data-id="element-396">
                  <div className="flex items-center" data-id="element-397">
                    <span className={`w-10 h-10 rounded-full ${data.bgColor} ${data.color} flex items-center justify-center font-bold mr-3`} data-id="element-398">
                      {i + 1}
                    </span>
                    <span className="font-bold text-slate-700" data-id="element-399">{song}</span>
                  </div>
                  <Play size={20} className="text-slate-300 group-hover:text-blue-500" fill="currentColor" data-id="element-400" />
                </motion.button>)}
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen w-full p-4 md:p-8 flex flex-col bg-slate-50" data-id="element-401">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 max-w-6xl mx-auto w-full" data-id="element-402">
        <button onClick={onBack} className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 bg-white px-4 py-2 rounded-full hover:bg-slate-100 transition-colors shadow-sm" data-id="element-403">
          <ArrowLeft size={20} data-id="element-404" />
          <span className="font-bold text-sm" data-id="element-405">返回</span>
        </button>
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-406">{data.name}入门</h1>
        <div className="w-20" data-id="element-407"></div>
      </div>

      <div className="max-w-6xl mx-auto w-full flex-1" data-id="element-408">
        {/* Main Content - Horizontal Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-id="element-409">
          {/* Left: Instrument Display */}
          <div className="lg:col-span-2" data-id="element-410">
            <Card className={`h-full min-h-[400px] ${data.bgColor} flex flex-col items-center justify-center relative overflow-hidden border-0`} data-id="element-411">
              <motion.div animate={isPlaying ? {
              scale: [1, 1.1, 1],
              rotate: [0, 2, -2, 0]
            } : {}} transition={{
              duration: 0.3,
              repeat: isPlaying ? Infinity : 0
            }} className={`text-8xl md:text-9xl mb-4 cursor-pointer select-none ${data.color}`} style={{
              fontFamily: 'serif'
            }} onClick={handlePlay} data-id="element-412">
                {data.icon}
              </motion.div>

              {/* Sound Wave */}
              <div className="flex items-center justify-center space-x-1 h-10 mb-4" data-id="element-413">
                {data.audioFrequencies.map((_, index) => <motion.div key={index} animate={{
                height: currentNote === index ? 40 : isPlaying ? 20 : 6,
                backgroundColor: currentNote === index ? '#3B82F6' : '#CBD5E1'
              }} className="w-2 rounded-full" style={{
                height: 6
              }} data-id="element-414" />)}
              </div>

              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={handlePlay} disabled={isPlaying} className={`px-6 py-3 rounded-full font-bold shadow-lg transition-all flex items-center ${isPlaying ? 'bg-white/50 text-slate-500' : 'bg-white text-slate-800 hover:shadow-xl'}`} data-id="element-415">
                {isPlaying ? <>
                    <Volume2 className="mr-2 animate-pulse" size={18} data-id="element-416" />{' '}
                    演奏中...
                  </> : <>
                    <Play className="mr-2" size={18} fill="currentColor" data-id="element-417" /> 试听
                    {data.name}
                  </>}
              </motion.button>

              <div className={`mt-4 px-4 py-2 rounded-full text-sm font-medium ${data.bgColor} ${data.color} border border-current/20`} data-id="element-418">
                {data.soundDesc}
              </div>
            </Card>
          </div>

          {/* Right: Info & Exercises */}
          <div className="lg:col-span-3 space-y-4" data-id="element-419">
            {/* Description */}
            <Card className="p-5 bg-white" data-id="element-420">
              <h3 className="font-bold text-lg mb-3 flex items-center text-slate-800" data-id="element-421">
                <Info size={20} className="mr-2 text-blue-500" data-id="element-422" />
                乐器介绍
              </h3>
              <p className="text-slate-600 leading-relaxed text-sm" data-id="element-423">
                {data.desc}
              </p>
            </Card>

            {/* Exercises Grid */}
            <div className="grid grid-cols-2 gap-3" data-id="element-424">
              {exercises.map(ex => {
              const Icon = ex.icon;
              return <motion.button key={ex.id} whileHover={{
                y: -2
              }} whileTap={{
                scale: 0.98
              }} onClick={() => setActiveExercise(ex.id)} className={`p-4 rounded-xl text-left transition-all ${activeExercise === ex.id ? `${data.bgColor} border-2 border-current/30` : 'bg-white border border-slate-100 hover:shadow-md'}`} data-id="element-425">
                    <div className={`w-10 h-10 rounded-lg ${activeExercise === ex.id ? 'bg-white' : data.bgColor} flex items-center justify-center mb-2`} data-id="element-426">
                      <Icon size={20} className={data.color} data-id="element-427" />
                    </div>
                    <h4 className="font-bold text-slate-800" data-id="element-428">{ex.name}</h4>
                    <p className="text-xs text-slate-500 mt-1" data-id="element-429">{ex.desc}</p>
                  </motion.button>;
            })}
            </div>

            {/* Quick Play */}
            <Card className="p-5 bg-white" data-id="element-430">
              <h3 className="font-bold text-lg mb-3 flex items-center text-slate-800" data-id="element-431">
                <Volume2 size={20} className="mr-2 text-green-500" data-id="element-432" />
                快速试听
              </h3>
              <div className="flex justify-between gap-2" data-id="element-433">
                {['Do', 'Re', 'Mi', 'Sol', 'La'].map((note, i) => <motion.button key={note} whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.9
              }} onClick={() => playNote(data.audioFrequencies[i], instrumentId)} className={`flex-1 aspect-square max-w-[60px] ${data.bgColor} border border-slate-200 rounded-xl text-sm font-bold ${data.color} hover:shadow-md transition-all`} data-id="element-434">
                    {note}
                  </motion.button>)}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Exercise Modal */}
      <AnimatePresence data-id="element-435">
        {activeExercise && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => {
        setActiveExercise(null);
        setQuizAnswer(null);
        setQuizCorrect(null);
      }} data-id="element-436">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" data-id="element-437">
              <div className="flex justify-between items-center mb-4" data-id="element-438">
                <h3 className="font-bold text-xl text-slate-800" data-id="element-439">互动练习</h3>
                <button onClick={() => {
              setActiveExercise(null);
              setQuizAnswer(null);
              setQuizCorrect(null);
            }} className="p-2 hover:bg-slate-100 rounded-full" data-id="element-440">
                  <X size={20} data-id="element-441" />
                </button>
              </div>
              {renderExerciseContent()}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}